import { Routes, Route, useLocation, Outlet, Link, Navigate } from 'react-router-dom'
import { useEffect, useState, lazy, Suspense } from 'react'
import { XCircle } from 'lucide-react'
import { Navbar } from './components/Navbar'
import { Footer } from './components/Footer'
import { CookieBanner } from './components/CookieBanner'
import { SeoHead } from './seo/SeoHead'
import { CrmRoleProvider } from './context/CrmRoleContext'
import { CrmUnreadProvider } from './context/CrmUnreadContext'
import { RequireRole } from './components/auth/RequireRole'
import { ErrorBoundary } from './components/ErrorBoundary'
import { RouteFallback } from './components/RouteFallback'
import { RouteErrorFallback } from './components/RouteErrorFallback'
import { readAuthLinkError } from './lib/authLinkError'
import { useTranslation } from './i18n'

import HomePage from './pages/HomePage'
const ServicesPage = lazy(() => import('./pages/ServicesPage'))
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'))
const OurWorkPage = lazy(() => import('./pages/OurWorkPage')) // LOCAL ONLY - do not commit
const AboutPage = lazy(() => import('./pages/AboutPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const ConfirmEmailPage = lazy(() => import('./pages/ConfirmEmailPage'))
const SetPasswordPage = lazy(() => import('./pages/SetPasswordPage'))
// The chat widget is not needed for first paint: load its code only after the
// page has loaded and gone idle, or on the visitor's first interaction.
const SupportWidget = lazy(() =>
  import('./components/support/SupportWidget').then(m => ({ default: m.SupportWidget })),
)

function DeferredSupportWidget() {
  const [ready, setReady] = useState(false)
  useEffect(() => {
    if (ready) return
    const go = () => setReady(true)
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const
    events.forEach(e => window.addEventListener(e, go, { once: true, passive: true }))
    let timer: number | undefined
    const onLoad = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback
      timer = window.setTimeout(() => (ric ? ric(go) : go()), 2000)
    }
    if (document.readyState === 'complete') onLoad()
    else window.addEventListener('load', onLoad, { once: true })
    return () => {
      events.forEach(e => window.removeEventListener(e, go))
      window.removeEventListener('load', onLoad)
      if (timer) window.clearTimeout(timer)
    }
  }, [ready])
  if (!ready) return null
  return (
    <Suspense fallback={null}>
      <SupportWidget />
    </Suspense>
  )
}
const NotFound = lazy(() => import('./pages/NotFound'))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'))
const CrmDashboardPage = lazy(() => import('./pages/crm/CrmDashboardPage'))
const CrmClientsPage = lazy(() => import('./pages/crm/CrmClientsPage'))
const CrmClientDetailPage = lazy(() => import('./pages/crm/CrmClientDetailPage'))
const CrmCompaniesPage = lazy(() => import('./pages/crm/CrmCompaniesPage'))
const CrmCompanyDetailPage = lazy(() => import('./pages/crm/CrmCompanyDetailPage'))
const CrmOrdersPage = lazy(() => import('./pages/crm/CrmOrdersPage'))
const CrmOrderDetailPage = lazy(() => import('./pages/crm/CrmOrderDetailPage'))
const CrmProjectsPage = lazy(() => import('./pages/crm/CrmProjectsPage'))
const CrmProjectDetailPage = lazy(() => import('./pages/crm/CrmProjectDetailPage'))
const CrmTasksPage = lazy(() => import('./pages/crm/CrmTasksPage'))
const CrmCalendarPage = lazy(() => import('./pages/crm/CrmCalendarPage'))
const CrmCommunicationsPage = lazy(() => import('./pages/crm/CrmCommunicationsPage'))
const CrmSupportPage = lazy(() => import('./pages/crm/CrmSupportPage'))
const CrmFilesPage = lazy(() => import('./pages/crm/CrmFilesPage'))
const CrmDeliveriesPage = lazy(() => import('./pages/crm/CrmDeliveriesPage'))
const CrmTeamPage = lazy(() => import('./pages/crm/CrmTeamPage'))

const CrmSettingsPage = lazy(() => import('./pages/crm/CrmSettingsPage'))
const PortalDashboardPage = lazy(() => import('./pages/portal/PortalDashboardPage'))
const PortalOrdersPage = lazy(() => import('./pages/portal/PortalOrdersPage'))
const PortalOrderWizardPage = lazy(() => import('./pages/portal/PortalOrderWizardPage'))
const PortalCalendarPage = lazy(() => import('./pages/portal/PortalCalendarPage'))
const PortalMessagesPage = lazy(() => import('./pages/portal/PortalMessagesPage'))
const PortalSettingsPage = lazy(() => import('./pages/portal/PortalSettingsPage'))

function PortalProfileRedirect() {
  const { search } = useLocation()
  return <Navigate to={'/portal/settings' + search} replace />
}

function StaffGuard() {
  return (
    <RequireRole allow="staff">
      <Outlet />
    </RequireRole>
  )
}


function ClientGuard() {
  return (
    <RequireRole allow="client">
      <Outlet />
    </RequireRole>
  )
}

/**
 * A second layout guard, not a per-route wrapper — the shape Lovable's route
 * parser understands. An employee reaching an admin-only URL is redirected to
 * /crm rather than shown a page whose every request will be refused.
 */
function AdminGuard() {
  return (
    <RequireRole allow="admin">
      <Outlet />
    </RequireRole>
  )
}

function ScrollToTop() {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0)
      return
    }

    // Lazy routes mount after this effect runs, so retry briefly until the target exists.
    const id = hash.slice(1)
    let frames = 0
    let raf = 0

    const findTarget = () => {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      } else if (frames++ < 180) {
        raf = requestAnimationFrame(findTarget)
      }
    }

    raf = requestAnimationFrame(findTarget)
    return () => cancelAnimationFrame(raf)
  }, [pathname, hash])

  return null
}

/**
 * An expired or already-used email link (signup confirmation, invite, reset)
 * comes back from Supabase with the failure in the URL, e.g.
 *   /portal#error=access_denied&error_code=otp_expired&...
 * Without this gate the visitor hits a route guard, gets bounced to /login and
 * never learns why. readAuthLinkError() strips the params, so this reads them
 * once on mount and — when present — replaces the whole page with the
 * explanation. Reset/invite links land on pages that already handle this
 * themselves; this catches everything else (notably signup confirmation).
 */
function AuthLinkErrorGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  // /set-password and /reset-password read the same params themselves and show
  // copy specific to invites and resets — leave those URLs to them.
  const selfHandled = pathname === '/set-password' || pathname === '/reset-password' || pathname === '/confirm-email'
  const [linkError] = useState(() => (selfHandled ? null : readAuthLinkError()))

  if (!linkError) return <>{children}</>

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background">
      <div className="max-w-md w-full glass-effect rounded-2xl p-8 sm:p-10 text-center">
        <div className="w-14 h-14 bg-destructive/10 rounded-xl flex items-center justify-center mx-auto mb-5">
          <XCircle className="w-7 h-7 text-destructive-text" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-3">
          {t.loginPage.confirmLinkInvalidTitle}
        </h1>
        <p className="text-muted-foreground text-sm leading-relaxed mb-8">
          {t.loginPage.confirmLinkInvalidMessage}
        </p>
        <Link
          to="/login"
          className="inline-block w-full bg-primary text-primary-foreground font-semibold px-8 py-3 rounded-lg hover:opacity-90 gentle-animation text-sm text-center shadow-lg shadow-primary/20"
        >
          {t.loginPage.backToSignIn}
        </Link>
      </div>
    </div>
  )
}

export default function App() {
  const { pathname } = useLocation()
  const isCrm = pathname === '/crm' || pathname.startsWith('/crm/')
  const isPortal = pathname === '/portal' || pathname.startsWith('/portal/')

  if (isCrm || isPortal) {
    return (
      <CrmRoleProvider>
        <CrmUnreadProvider>
        <AuthLinkErrorGate>
        <div className="min-h-screen bg-background text-foreground">

          <ScrollToTop />
          <ErrorBoundary fallback={reset => <RouteErrorFallback onRetry={reset} />}>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route element={<StaffGuard />}>
                <Route path="/crm" element={<CrmDashboardPage />} />
                <Route path="/crm/clients" element={<CrmClientsPage />} />
                <Route path="/crm/clients/:clientId" element={<CrmClientDetailPage />} />
                <Route path="/crm/companies" element={<CrmCompaniesPage />} />
                <Route path="/crm/companies/:companyId" element={<CrmCompanyDetailPage />} />
                <Route path="/crm/orders" element={<CrmOrdersPage />} />
                <Route path="/crm/orders/:orderId" element={<CrmOrderDetailPage />} />
                <Route path="/crm/projects" element={<CrmProjectsPage />} />
                <Route path="/crm/projects/:projectId" element={<CrmProjectDetailPage />} />
                <Route path="/crm/tasks" element={<CrmTasksPage />} />
                <Route path="/crm/calendar" element={<CrmCalendarPage />} />
                <Route path="/crm/communications" element={<CrmCommunicationsPage />} />
                <Route path="/crm/support" element={<CrmSupportPage />} />
                <Route path="/crm/files" element={<CrmFilesPage />} />
                <Route path="/crm/deliveries" element={<CrmDeliveriesPage />} />
                <Route path="/crm/settings" element={<CrmSettingsPage />} />
              </Route>
              {/* The bin moved to a Settings tab on 5 October 2026; this keeps
                  old links and bookmarks working. It stays inside AdminGuard so
                  an employee following it lands on /crm, as before. A guard
                  group is the same layout-route shape StaffGuard uses, so
                  Lovable's parser still sees each child; it is per-route
                  <RequireRole> wrappers that break it. */}
              <Route element={<AdminGuard />}>
                {/* Hidden from employees in the sidebar since it was built; until
                    8 Oct 2026 its address still opened it for them. */}
                <Route path="/crm/team" element={<CrmTeamPage />} />
                <Route
                  path="/crm/recently-deleted"
                  element={<Navigate to="/crm/settings?tab=deleted" replace />}
                />
              </Route>
              <Route element={<ClientGuard />}>
                <Route path="/portal" element={<PortalDashboardPage />} />
                <Route path="/portal/orders" element={<PortalOrdersPage />} />
                <Route path="/portal/orders/new/:slug" element={<PortalOrderWizardPage />} />
                {/* Deliveries and Files moved onto each order. The delivery push
                    notification and old links still point here. */}
                <Route path="/portal/deliveries" element={<Navigate to="/portal/orders" replace />} />
                <Route path="/portal/calendar" element={<PortalCalendarPage />} />
                <Route path="/portal/messages" element={<PortalMessagesPage />} />
                <Route path="/portal/files" element={<Navigate to="/portal/orders" replace />} />
                <Route path="/portal/settings" element={<PortalSettingsPage />} />
                {/* "My profile" became Settings on 30 October 2026; old links and
                    bookmarks, with their ?tab=, still land on it. */}
                <Route path="/portal/profile" element={<PortalProfileRedirect />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </div>
        </AuthLinkErrorGate>
        </CrmUnreadProvider>
      </CrmRoleProvider>

    )
  }


  return (
    <AuthLinkErrorGate>
    <div className="min-h-screen bg-background text-foreground">
      <ScrollToTop />
      <SeoHead />
      <Navbar />
      <main>
        {/* Scoped to the routed area so a throw inside one page keeps the navbar
            and footer — the visitor can still navigate out of it. */}
        <ErrorBoundary fallback={reset => <RouteErrorFallback onRetry={reset} />}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
            <Route path="/portfolio" element={<OurWorkPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/confirm-email" element={<ConfirmEmailPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            {/* Lives in the public tree on purpose: App only renders the CRM
                shell for /crm and /portal paths, so this is where RequireRole's
                redirect actually lands. */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
      <CookieBanner />
      <DeferredSupportWidget />
    </div>
    </AuthLinkErrorGate>
  )
}
